import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast as message } from 'sonner'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Modal } from '../../components/ui/modal'
import { Field, SelectField, Textarea } from '../../components/ui/controls'
import { DataTable, type Column } from '../../components/ui/data-table'
import {
  addBankAccount,
  addPersonalPayoutAccount,
  createPayoutEwallet,
  deleteBankAccount,
  deletePersonalPayoutAccount,
  deletePayoutEwallet,
  getBankAccounts,
  getPayoutEwallet,
  getPersonalPayoutAccounts,
} from '../../services/payout'
import type { PayoutEwallet } from '../../services/payout'
import { getPersonalKycStatus } from '../../services/kyc'
import { useAuthStore } from '../../stores/authStore'
import type { BankAccount, PersonalPayoutAccount } from '../../types'
import './BankAccounts.css'

const REGION_OPTIONS = [
  { value: 'US', label: '美國（US）' },
  { value: 'HK', label: '中國香港（HK）' },
  { value: 'SG', label: '新加坡（SG）' },
]

const COUNTRY_OPTIONS = [
  { value: 'CN', label: '🇨🇳 中國' },
  { value: 'HK', label: '🇭🇰 中國香港' },
  { value: 'SG', label: '🇸🇬 新加坡' },
  { value: 'US', label: '🇺🇸 美國' },
]

const PERSONAL_BANK_COUNTRIES = [
  { value: 'CN', label: '🇨🇳 中國', phoneCode: '+86' },
  { value: 'HK', label: '🇭🇰 中國香港', phoneCode: '+852' },
  { value: 'MO', label: '🇲🇴 中國澳門', phoneCode: '+853' },
  { value: 'TW', label: '🇹🇼 中國台灣', phoneCode: '+886' },
  { value: 'US', label: '🇺🇸 美國', phoneCode: '+1' },
  { value: 'SG', label: '🇸🇬 新加坡', phoneCode: '+65' },
  { value: 'GB', label: '🇬🇧 英國', phoneCode: '+44' },
  { value: 'FR', label: '🇫🇷 法國', phoneCode: '+33' },
  { value: 'JP', label: '🇯🇵 日本', phoneCode: '+81' },
  { value: 'KR', label: '🇰🇷 韓國', phoneCode: '+82' },
]

const PHONE_CODE_OPTIONS = PERSONAL_BANK_COUNTRIES.map(
  ({ label, phoneCode }) => ({
    value: phoneCode,
    label: `${label.split(' ')[0]} ${phoneCode}`,
  }),
)

const EWALLET_METHODS = [
  {
    value: 'ALIPAY',
    label: 'Alipay',
    currency: 'CNY',
    fieldKey: 'account_no',
    fieldLabel: '支付寶賬號（手機號或郵箱）',
  },
  {
    value: 'PAYPAL',
    label: 'PayPal',
    currency: 'USD',
    fieldKey: 'account_no',
    fieldLabel: 'PayPal 賬號郵箱',
  },
]

const ewalletMethodForCountry = (country?: string) =>
  country === 'CN' ? 'ALIPAY' : 'PAYPAL'

const ewalletMethodsForCountry = (country?: string) => {
  const method = ewalletMethodForCountry(country)
  return EWALLET_METHODS.filter((item) => item.value === method)
}

const ENGLISH_LETTERS_ONLY_PATTERN = /^[A-Za-z]+$/
const ENGLISH_BANK_NAME_PATTERN = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/
const CHINESE_NAME_PATTERN = /^[\u3400-\u9FFF]+$/
const MOBILE_PATTERN = /^\+\d{8,15}$/
const TRON_ADDRESS_PATTERN = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/

interface PersonalKycStatus {
  status?: string
}

interface RecipientValues {
  recipientType: 'INDIVIDUAL' | 'BUSINESS'
  accountKind: 'BANK' | 'E_WALLET'
  country: string
  currency: string
  firstName?: string
  lastName?: string
  localFirstName?: string
  localLastName?: string
  phoneCountryCode?: string
  phoneNumber?: string
  address?: string
}

interface PersonalAccountValues {
  paymentMethod: string
  accountNo?: string
  bankAccountNo?: string
  bankName?: string
  bankCode?: string
  certNumber?: string
  isDefault?: boolean
}

export default function BankAccounts() {
  const [accounts, setAccounts] = useState<BankAccount[]>([])
  const [personalAccounts, setPersonalAccounts] = useState<
    PersonalPayoutAccount[]
  >([])
  const [payoutEwallet, setPayoutEwallet] = useState<PayoutEwallet | null>(null)
  const [payoutEwalletLoading, setPayoutEwalletLoading] = useState(true)
  const [payoutEwalletLoadError, setPayoutEwalletLoadError] = useState(false)
  const [personalKycStatus, setPersonalKycStatus] =
    useState<PersonalKycStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [modalVisible, setModalVisible] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [region, setRegion] = useState('US')
  const [personalModalVisible, setPersonalModalVisible] = useState(false)
  const [payoutEwalletModalVisible, setPayoutEwalletModalVisible] =
    useState(false)
  const [deletePayoutEwalletModalVisible, setDeletePayoutEwalletModalVisible] =
    useState(false)
  const [personalStep, setPersonalStep] = useState(0)
  const [personalKind, setPersonalKind] = useState<'BANK' | 'E_WALLET'>(
    'E_WALLET',
  )
  const [ewalletMethod, setEwalletMethod] = useState('ALIPAY')
  const [recipientValues, setRecipientValues] =
    useState<RecipientValues | null>(null)
  const [accountValues, setAccountValues] = useState<
    Partial<PersonalAccountValues>
  >({ paymentMethod: 'ALIPAY' })
  const form = useForm<{
    accountHolderName: string
    bankName: string
    bankCode: string
    branchCode?: string
    accountNumber: string
    region: string
    fedWire?: string
    bankAddress?: string
  }>({ defaultValues: { region: 'US' } })
  const recipientForm = useForm<RecipientValues>({
    defaultValues: {
      recipientType: 'INDIVIDUAL',
      accountKind: 'E_WALLET',
      country: 'CN',
      currency: 'CNY',
      phoneCountryCode: '+86',
    },
  })
  const accountForm = useForm<PersonalAccountValues>({
    defaultValues: { paymentMethod: 'ALIPAY' },
  })
  const payoutEwalletForm = useForm<{
    chain: PayoutEwallet['chain']
    address: string
    totpCode: string
  }>({ defaultValues: { chain: 'TRON' } })
  const deletePayoutEwalletForm = useForm<{ totpCode: string }>()
  const twoFactorEnabled = useAuthStore(
    (state) => state.merchant?.twoFactorEnabled === true,
  )

  const personalKycApproved = personalKycStatus?.status === 'APPROVED'
  const personalBankCountry = recipientForm.watch('country')
  const isInternationalBank =
    personalKind === 'BANK' &&
    (personalStep === 0 ? personalBankCountry : recipientValues?.country) !==
      'CN'

  const loadPayoutEwallet = async () => {
    setPayoutEwalletLoading(true)
    setPayoutEwalletLoadError(false)
    try {
      const response = await getPayoutEwallet()
      setPayoutEwallet(response.data ?? null)
    } catch {
      setPayoutEwalletLoadError(true)
    } finally {
      setPayoutEwalletLoading(false)
    }
  }

  const fetchAccounts = () => {
    setLoading(true)
    Promise.all([
      getBankAccounts(),
      getPersonalPayoutAccounts(),
      getPersonalKycStatus(),
    ])
      .then(([bankRes, personalRes, kycRes]) => {
        setAccounts(bankRes.data)
        setPersonalAccounts(personalRes.data)
        setPersonalKycStatus(kycRes.data)
      })
      .catch(() => message.error('賬戶資料載入失敗'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchAccounts()
    loadPayoutEwallet()
  }, [])

  const handleAdd = async (values: {
    accountHolderName: string
    bankName: string
    bankCode: string
    branchCode?: string
    accountNumber: string
    region: string
    fedWire?: string
    bankAddress?: string
    isDefault?: boolean
  }) => {
    setSubmitting(true)
    try {
      await addBankAccount(values)
      message.success('銀行賬戶添加成功')
      setModalVisible(false)
      form.reset()
      setRegion('US')
      fetchAccounts()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '添加失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteBankAccount(id)
      message.success('銀行賬戶已刪除')
      fetchAccounts()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '刪除失敗')
    }
  }

  const handleDeletePersonalAccount = async (id: string) => {
    try {
      await deletePersonalPayoutAccount(id)
      message.success('個人提款賬戶已刪除')
      fetchAccounts()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '刪除失敗')
    }
  }

  const handleCreatePayoutEwallet = async (values: {
    chain: PayoutEwallet['chain']
    address: string
    totpCode: string
  }) => {
    setSubmitting(true)
    try {
      const response = await createPayoutEwallet({
        chain: values.chain,
        address: values.address.trim(),
        totpCode: values.totpCode,
      })
      setPayoutEwallet(response.data)
      setPayoutEwalletModalVisible(false)
      payoutEwalletForm.reset()
      message.success('海外電子錢包地址已添加')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '添加失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeletePayoutEwallet = async (values: { totpCode: string }) => {
    setSubmitting(true)
    try {
      await deletePayoutEwallet({ totpCode: values.totpCode })
      setPayoutEwallet(null)
      setDeletePayoutEwalletModalVisible(false)
      deletePayoutEwalletForm.reset()
      message.success('海外電子錢包地址已刪除')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '刪除失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const copyPayoutEwalletAddress = async () => {
    if (!payoutEwallet) return
    try {
      await navigator.clipboard.writeText(payoutEwallet.address)
      message.success('地址已複製')
    } catch {
      message.error('無法複製地址')
    }
  }

  const handleCancel = () => {
    setModalVisible(false)
    form.reset()
    setRegion('US')
  }

  const openPersonalModal = () => {
    setPersonalStep(0)
    setPersonalKind('E_WALLET')
    setEwalletMethod('ALIPAY')
    setRecipientValues(null)
    setAccountValues({ paymentMethod: 'ALIPAY' })
    setPersonalModalVisible(true)
  }

  const closePersonalModal = () => {
    setPersonalModalVisible(false)
    recipientForm.reset()
    accountForm.reset()
    setPersonalStep(0)
    setRecipientValues(null)
    setAccountValues({ paymentMethod: 'ALIPAY' })
  }

  const handleRecipientNext = async () => {
    if (
      !(await recipientForm.trigger(
        personalKind === 'E_WALLET'
          ? ['firstName', 'lastName']
          : isInternationalBank
            ? ['firstName', 'lastName', 'phoneNumber']
            : ['localFirstName', 'localLastName', 'phoneNumber'],
      ))
    )
      return
    const values = recipientForm.getValues()
    const normalizedValues = {
      ...values,
      firstName: values.firstName?.trim(),
      lastName: values.lastName?.trim(),
      localFirstName: values.localFirstName?.trim(),
      localLastName: values.localLastName?.trim(),
    }
    if (values.accountKind === 'E_WALLET') {
      const method = ewalletMethodForCountry(values.country)
      setEwalletMethod(method)
      setAccountValues((prev) => ({ ...prev, paymentMethod: method }))
      accountForm.setValue('paymentMethod', method)
    }
    setRecipientValues(normalizedValues)
    setPersonalStep(1)
  }

  const handlePersonalSave = async () => {
    if (!personalKycApproved) {
      message.error('請先完成並通過個人 KYC，再添加個人提款賬戶')
      return
    }

    const recipient = recipientValues
    if (
      !recipient ||
      !recipient.country ||
      (recipient.accountKind === 'BANK' && !recipient.currency)
    ) {
      message.error('請先完成收款人信息')
      setPersonalStep(0)
      return
    }

    if (
      !(await accountForm.trigger(
        personalKind === 'E_WALLET'
          ? ['accountNo']
          : isInternationalBank
            ? ['bankName', 'bankCode', 'bankAccountNo']
            : ['bankAccountNo', 'certNumber'],
      ))
    )
      return
    const account = {
      ...accountValues,
      ...accountForm.getValues(),
    }
    const fullName = [recipient.firstName, recipient.lastName]
      .filter(Boolean)
      .join(' ')

    setSubmitting(true)
    try {
      if (recipient.accountKind === 'E_WALLET') {
        const accountNo = account.accountNo?.trim()
        if (!accountNo) {
          accountForm.setError('accountNo', { message: '請輸入收款賬號' })
          message.error('請輸入收款賬號')
          setSubmitting(false)
          return
        }

        const expectedMethod = ewalletMethodForCountry(recipient.country)
        const method =
          EWALLET_METHODS.find((item) => item.value === expectedMethod) ??
          EWALLET_METHODS[0]
        await addPersonalPayoutAccount({
          accountKind: 'E_WALLET',
          paymentMethod: method.value,
          country: recipient.country,
          currency: method.currency,
          accountHolderName: fullName,
          fields: [
            {
              key: method.fieldKey,
              value: accountNo,
              displayValue: accountNo,
            },
          ],
        })
      } else {
        const bankAccountNo = account.bankAccountNo?.trim()
        const certNumber = account.certNumber?.trim()
        const bankName = account.bankName?.trim()
        const bankCode = account.bankCode?.trim().toUpperCase()
        const isChinaBank = recipient.country === 'CN'
        const localFullName = [
          recipient.localLastName,
          recipient.localFirstName,
        ]
          .filter(Boolean)
          .join('')
        const mobile = `${recipient.phoneCountryCode || ''}${(recipient.phoneNumber || '').trim()}`
        const fieldErrors: Array<{
          name: keyof PersonalAccountValues
          errors: string[]
        }> = []
        if (!bankAccountNo)
          fieldErrors.push({
            name: 'bankAccountNo',
            errors: ['請輸入銀行賬號'],
          })
        if (isChinaBank && !certNumber)
          fieldErrors.push({ name: 'certNumber', errors: ['請輸入身份證號'] })
        if (!isChinaBank && !bankName)
          fieldErrors.push({ name: 'bankName', errors: ['請輸入銀行名稱'] })
        if (
          !isChinaBank &&
          (!bankCode || !/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(bankCode))
        ) {
          fieldErrors.push({
            name: 'bankCode',
            errors: ['請輸入有效的 SWIFT/BIC 代碼'],
          })
        }
        if (isChinaBank && !CHINESE_NAME_PATTERN.test(localFullName)) {
          recipientForm.setError('localFirstName', {
            message: '請輸入中文名字',
          })
          recipientForm.setError('localLastName', { message: '請輸入中文姓氏' })
          message.error('中國銀行賬戶姓名只能使用中文')
          setPersonalStep(0)
          setSubmitting(false)
          return
        }
        if (!MOBILE_PATTERN.test(mobile)) {
          recipientForm.setError('phoneNumber', {
            message: '請輸入有效的手機號',
          })
          message.error('請輸入有效的手機號')
          setPersonalStep(0)
          setSubmitting(false)
          return
        }
        if (fieldErrors.length > 0) {
          fieldErrors.forEach(({ name, errors }) =>
            accountForm.setError(name, { message: errors[0] }),
          )
          message.error('請完成收款方式信息')
          setSubmitting(false)
          return
        }

        await addPersonalPayoutAccount({
          accountKind: 'BANK',
          paymentMethod: 'BANK_TRANSFER',
          country: recipient.country,
          currency: recipient.currency,
          accountHolderName: isChinaBank ? localFullName : fullName,
          firstName: isChinaBank
            ? recipient.localFirstName
            : recipient.firstName,
          lastName: isChinaBank ? recipient.localLastName : recipient.lastName,
          mobile,
          accountNumber: bankAccountNo,
          bankName: isChinaBank ? undefined : bankName,
          bankCode: isChinaBank ? undefined : bankCode,
          clearingNetwork: isChinaBank ? undefined : 'SWIFT',
          certNumber: isChinaBank ? certNumber : undefined,
        })
      }
      message.success('個人提款賬戶已添加')
      closePersonalModal()
      fetchAccounts()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '添加失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const [deletingAccount, setDeletingAccount] = useState<{
    id: string
    personal: boolean
  } | null>(null)
  const bankColumns: Column<BankAccount>[] = [
    {
      key: 'name',
      header: '企業/賬戶名稱',
      render: (r) => r.accountHolderName,
    },
    { key: 'bank', header: '銀行', render: (r) => r.bankName },
    { key: 'swift', header: 'SWIFT/BIC', render: (r) => r.bankCode || '-' },
    { key: 'branch', header: '分行代碼', render: (r) => r.branchCode || '-' },
    {
      key: 'number',
      header: '賬號',
      render: (r) => `****${r.accountNumberLast4}`,
    },
    {
      key: 'region',
      header: '地區',
      render: (r) =>
        REGION_OPTIONS.find((o) => o.value === r.region)?.label ?? r.region,
    },
    { key: 'fedWire', header: 'FedWire', render: (r) => r.fedWire || '-' },
    {
      key: 'status',
      header: '狀態',
      render: (r) =>
        `${r.isDefault ? '預設 ' : ''}${r.isVerified ? '已驗證' : ''}`,
    },
    {
      key: 'actions',
      header: '操作',
      className: 'whitespace-nowrap',
      render: (r) => (
        <Button
          variant="destructive"
          onClick={() => setDeletingAccount({ id: r.id, personal: false })}
        >
          刪除
        </Button>
      ),
    },
  ]
  const personalColumns: Column<PersonalPayoutAccount>[] = [
    {
      key: 'kind',
      header: '類型',
      render: (r) => (r.accountKind === 'BANK' ? '銀行賬戶' : '電子錢包'),
    },
    { key: 'method', header: '方式', render: (r) => r.paymentMethod },
    { key: 'currency', header: '幣種', render: (r) => r.currency },
    {
      key: 'account',
      header: '賬戶',
      render: (r) =>
        r.accountKind === 'BANK'
          ? `${r.bankName || 'Bank Account'} ****${r.accountNumberLast4 || ''}`
          : r.fields?.[0]?.displayValue || r.fields?.[0]?.value || '-',
    },
    {
      key: 'actions',
      header: '操作',
      className: 'whitespace-nowrap',
      render: (r) => (
        <Button
          variant="destructive"
          onClick={() => setDeletingAccount({ id: r.id, personal: true })}
        >
          刪除
        </Button>
      ),
    },
  ]
  const recipientInput = (
    name: keyof RecipientValues,
    label: string,
    pattern?: RegExp,
    required = true,
  ) => (
    <Field
      label={label}
      htmlFor={`recipient-${name}`}
      required={required}
      error={recipientForm.formState.errors[name]?.message}
    >
      <Input
        id={`recipient-${name}`}
        {...recipientForm.register(name, {
          required: required && `請輸入${label}`,
          validate: (value) =>
            !required ||
            (typeof value === 'string' && !!value.trim()) ||
            `請輸入${label}`,
          pattern: pattern
            ? { value: pattern, message: `${label}格式不正確` }
            : undefined,
        })}
      />
    </Field>
  )
  const accountInput = (
    name: keyof PersonalAccountValues,
    label: string,
    pattern?: RegExp,
  ) => (
    <Field
      label={label}
      htmlFor={`account-${name}`}
      required
      error={accountForm.formState.errors[name]?.message}
    >
      <Input
        id={`account-${name}`}
        {...accountForm.register(name, {
          required: `請輸入${label}`,
          validate: (value) =>
            (typeof value === 'string' && !!value.trim()) || `請輸入${label}`,
          pattern: pattern
            ? { value: pattern, message: `${label}格式不正確` }
            : undefined,
        })}
      />
    </Field>
  )
  const changeKind = (value: 'BANK' | 'E_WALLET') => {
    setPersonalKind(value)
    setRecipientValues(null)
    recipientForm.reset({
      recipientType: 'INDIVIDUAL',
      accountKind: value,
      country: 'CN',
      currency: 'CNY',
      phoneCountryCode: '+86',
    })
    setEwalletMethod('ALIPAY')
    setAccountValues({
      paymentMethod: value === 'BANK' ? 'BANK_TRANSFER' : 'ALIPAY',
    })
    accountForm.reset({
      paymentMethod: value === 'BANK' ? 'BANK_TRANSFER' : 'ALIPAY',
    })
  }
  const changeCountry = (country: string) => {
    recipientForm.setValue('country', country)
    if (personalKind === 'BANK') {
      recipientForm.reset({
        ...recipientForm.getValues(),
        country,
        currency: country === 'CN' ? 'CNY' : 'USD',
        phoneCountryCode: PERSONAL_BANK_COUNTRIES.find(
          (o) => o.value === country,
        )?.phoneCode,
        phoneNumber: '',
        firstName: '',
        lastName: '',
        localFirstName: '',
        localLastName: '',
      })
      accountForm.reset({ paymentMethod: 'BANK_TRANSFER' })
      setAccountValues({ paymentMethod: 'BANK_TRANSFER' })
    } else {
      const method = ewalletMethodForCountry(country)
      setEwalletMethod(method)
      setAccountValues((prev) => ({ ...prev, paymentMethod: method }))
      accountForm.setValue('paymentMethod', method)
    }
  }
  const walletTotp = (remove = false) => {
    const target = remove ? deletePayoutEwalletForm : payoutEwalletForm
    return (
      <Field
        label="2FA 驗證碼"
        htmlFor={remove ? 'delete-totp' : 'wallet-totp'}
        required
        error={target.formState.errors.totpCode?.message}
      >
        <Input
          id={remove ? 'delete-totp' : 'wallet-totp'}
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          {...(remove
            ? deletePayoutEwalletForm.register('totpCode', {
                required: '請輸入 2FA 驗證碼',
                pattern: { value: /^\d{6}$/, message: '請輸入 6 位數字驗證碼' },
              })
            : payoutEwalletForm.register('totpCode', {
                required: '請輸入 2FA 驗證碼',
                pattern: { value: /^\d{6}$/, message: '請輸入 6 位數字驗證碼' },
              }))}
        />
      </Field>
    )
  }
  return (
    <div className="bank-accounts-page space-y-6">
      <h1 className="text-2xl font-semibold">銀行賬戶</h1>
      <Card className="bank-account-section">
        <div className="account-section-header">
          <h2>海外電子錢包</h2>
          {!payoutEwallet && !payoutEwalletLoadError && (
            <Button
              disabled={!twoFactorEnabled || payoutEwalletLoading}
              onClick={() => setPayoutEwalletModalVisible(true)}
            >
              添加地址
            </Button>
          )}
        </div>
        <div className="px-5 space-y-4">
          {payoutEwalletLoading && <p role="status">載入中…</p>}
          {payoutEwalletLoadError && (
            <div role="alert">
              海外電子錢包地址載入失敗{' '}
              <Button variant="outline" onClick={loadPayoutEwallet}>
                重新載入
              </Button>
            </div>
          )}
          {!twoFactorEnabled && (
            <p className="rounded-lg bg-amber-50 p-3 text-amber-900">
              添加或刪除地址前需要啟用 2FA{' '}
              <a className="text-primary underline" href="/dashboard/settings">
                前往安全設置
              </a>
            </p>
          )}
          {!payoutEwalletLoadError && payoutEwallet ? (
            <dl className="space-y-3">
              <div>
                <dt>鏈</dt>
                <dd>{payoutEwallet.chain}</dd>
              </div>
              <div>
                <dt>錢包地址</dt>
                <dd className="break-all">
                  {payoutEwallet.address}{' '}
                  <Button variant="ghost" onClick={copyPayoutEwalletAddress}>
                    複製地址
                  </Button>
                </dd>
              </div>
              <Button
                variant="destructive"
                disabled={!twoFactorEnabled}
                onClick={() => setDeletePayoutEwalletModalVisible(true)}
              >
                刪除地址
              </Button>
            </dl>
          ) : (
            !payoutEwalletLoadError &&
            twoFactorEnabled && (
              <p className="text-muted-foreground">尚未添加海外電子錢包地址</p>
            )
          )}
        </div>
      </Card>
      <Card className="bank-account-section">
        <div className="account-section-header">
          <h2>企業銀行賬戶</h2>
          <Button
            onClick={() => setModalVisible(true)}
            data-testid="add-bank-account-btn"
          >
            添加企業賬戶
          </Button>
        </div>
        <DataTable
          columns={bankColumns}
          data={accounts}
          rowKey="id"
          loading={loading}
        />
      </Card>
      <Card className="bank-account-section">
        <div className="account-section-header">
          <h2>個人提款賬戶</h2>
          <Button onClick={openPersonalModal}>添加個人提款賬戶</Button>
        </div>
        <DataTable
          columns={personalColumns}
          data={personalAccounts}
          rowKey="id"
          loading={loading}
        />
      </Card>
      <Modal
        title="添加海外電子錢包地址"
        open={payoutEwalletModalVisible}
        onOpenChange={(open) => {
          setPayoutEwalletModalVisible(open)
          if (!open) payoutEwalletForm.reset()
        }}
      >
        <form
          className="space-y-4"
          onSubmit={payoutEwalletForm.handleSubmit(handleCreatePayoutEwallet)}
        >
          <Field label="鏈" htmlFor="wallet-chain" required>
            <Controller
              name="chain"
              control={payoutEwalletForm.control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectField
                  id="wallet-chain"
                  value={field.value}
                  onValueChange={(v) => {
                    field.onChange(v)
                    void payoutEwalletForm.trigger('address')
                  }}
                  options={['TRON', 'BSC', 'ETH'].map((value) => ({
                    value,
                    label: value,
                  }))}
                />
              )}
            />
          </Field>
          <Field
            label="錢包地址"
            htmlFor="wallet-address"
            required
            error={payoutEwalletForm.formState.errors.address?.message}
          >
            <Input
              id="wallet-address"
              autoComplete="off"
              {...payoutEwalletForm.register('address', {
                required: '請輸入錢包地址',
                validate: (value) =>
                  (payoutEwalletForm.getValues('chain') === 'TRON'
                    ? TRON_ADDRESS_PATTERN
                    : EVM_ADDRESS_PATTERN
                  ).test(value.trim()) || '錢包地址與所選鏈不匹配',
              })}
            />
          </Field>
          {walletTotp()}
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting}>
              保存
            </Button>
            <Button
              variant="outline"
              onClick={() => setPayoutEwalletModalVisible(false)}
            >
              取消
            </Button>
          </div>
        </form>
      </Modal>
      <Modal
        title="確認刪除海外電子錢包地址"
        open={deletePayoutEwalletModalVisible}
        onOpenChange={(open) => {
          setDeletePayoutEwalletModalVisible(open)
          if (!open) deletePayoutEwalletForm.reset()
        }}
      >
        <p className="mb-4 rounded-lg bg-amber-50 p-3">
          刪除後，新的 USDT 結款將無法提交，直至重新添加地址。
        </p>
        <form
          className="space-y-4"
          onSubmit={deletePayoutEwalletForm.handleSubmit(
            handleDeletePayoutEwallet,
          )}
        >
          {walletTotp(true)}
          <Button type="submit" variant="destructive" disabled={submitting}>
            確認刪除
          </Button>
        </form>
      </Modal>
      <Modal
        title="添加銀行賬戶（企業賬戶）"
        open={modalVisible}
        onOpenChange={(open) => {
          if (!open) handleCancel()
        }}
      >
        <form className="space-y-4" onSubmit={form.handleSubmit(handleAdd)}>
          {(
            [
              ['accountHolderName', '企業/賬戶名稱'],
              ['bankName', '銀行名稱'],
              ['bankCode', 'SWIFT / BIC 代碼'],
              ['branchCode', '分行代碼（Branch Code）'],
              ['accountNumber', '銀行賬號'],
            ] as const
          ).map(([name, label]) => (
            <Field
              key={name}
              label={label}
              htmlFor={`business-${name}`}
              error={form.formState.errors[name]?.message}
            >
              <Input
                id={`business-${name}`}
                {...form.register(name, {
                  required: name !== 'branchCode' && `請輸入${label}`,
                })}
              />
            </Field>
          ))}
          <Field label="地區" htmlFor="business-region">
            <Controller
              name="region"
              control={form.control}
              render={({ field }) => (
                <SelectField
                  id="business-region"
                  value={field.value}
                  options={REGION_OPTIONS}
                  onValueChange={(val) => {
                    field.onChange(val)
                    setRegion(val)
                    if (val !== 'US') form.unregister('fedWire')
                  }}
                />
              )}
            />
          </Field>
          {region === 'US' && (
            <Field
              label="FedWire 路由號（ABA）"
              htmlFor="business-fedWire"
              error={form.formState.errors.fedWire?.message}
            >
              <Input
                id="business-fedWire"
                {...form.register('fedWire', {
                  required: region === 'US' && '請輸入 FedWire 路由號',
                })}
              />
            </Field>
          )}
          <Field label="銀行地址（Bank Address）" htmlFor="business-address">
            <Textarea
              id="business-address"
              rows={2}
              {...form.register('bankAddress')}
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting}>
              添加
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              取消
            </Button>
          </div>
        </form>
      </Modal>
      <Modal
        title="添加個人提款賬戶"
        className="sm:max-w-3xl"
        open={personalModalVisible}
        onOpenChange={(open) => {
          if (!open) closePersonalModal()
        }}
        footer={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={
                personalStep === 0
                  ? closePersonalModal
                  : () => {
                      setAccountValues(accountForm.getValues())
                      setPersonalStep(0)
                    }
              }
            >
              {' '}
              {personalStep === 0 ? '取消' : '返回'}
            </Button>
            {personalStep === 0 ? (
              <Button onClick={handleRecipientNext}>下一步</Button>
            ) : (
              <Button disabled={submitting} onClick={handlePersonalSave}>
                保存
              </Button>
            )}
          </div>
        }
      >
        {!personalKycApproved && (
          <p className="mb-4 rounded-lg bg-amber-50 p-3">
            個人 KYC 尚未通過。個人提款賬戶保存前需要先通過個人
            KYC。你仍可查看並準備表單字段。
          </p>
        )}
        <ol className="mb-6 flex gap-4 text-sm">
          <li
            className={
              personalStep === 0
                ? 'text-primary font-semibold'
                : 'text-muted-foreground'
            }
          >
            1. 設置收款人信息
          </li>
          <li
            className={
              personalStep === 1
                ? 'text-primary font-semibold'
                : 'text-muted-foreground'
            }
          >
            2. 設置收款方式
          </li>
        </ol>
        {personalStep === 0 ? (
          <div className="space-y-4">
            <Field label="您需要付款給誰？">
              <div className="flex gap-5">
                <label>
                  <input type="radio" checked readOnly /> 個人
                </label>
                <label className="text-muted-foreground">
                  <input type="radio" disabled /> 企業
                </label>
              </div>
            </Field>
            <Field label="收款賬戶類型">
              <div className="flex gap-5">
                {(['E_WALLET', 'BANK'] as const).map((kind) => (
                  <label key={kind}>
                    <input
                      type="radio"
                      name="accountKind"
                      checked={personalKind === kind}
                      onChange={() => changeKind(kind)}
                    />{' '}
                    {kind === 'BANK' ? '銀行賬戶' : '電子錢包'}
                  </label>
                ))}
              </div>
            </Field>
            <Field
              label={
                personalKind === 'BANK'
                  ? '銀行所在國家或地區'
                  : '所在國家或地區'
              }
              htmlFor="recipient-country"
            >
              <SelectField
                id="recipient-country"
                value={personalBankCountry}
                onValueChange={changeCountry}
                options={
                  personalKind === 'BANK'
                    ? PERSONAL_BANK_COUNTRIES
                    : COUNTRY_OPTIONS
                }
              />
            </Field>
            {personalKind === 'BANK' && (
              <Field label="幣種">
                <SelectField
                  value={recipientForm.watch('currency')}
                  onValueChange={(v) => recipientForm.setValue('currency', v)}
                  options={[
                    {
                      value: isInternationalBank ? 'USD' : 'CNY',
                      label: isInternationalBank ? 'USD' : 'CNY',
                    },
                  ]}
                />
              </Field>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {personalKind === 'BANK' && !isInternationalBank ? (
                <>
                  {recipientInput(
                    'localFirstName',
                    '名字',
                    CHINESE_NAME_PATTERN,
                  )}
                  {recipientInput(
                    'localLastName',
                    '姓氏',
                    CHINESE_NAME_PATTERN,
                  )}
                </>
              ) : (
                <>
                  {recipientInput(
                    'firstName',
                    isInternationalBank ? '英文名字' : '名字',
                    isInternationalBank
                      ? ENGLISH_BANK_NAME_PATTERN
                      : ENGLISH_LETTERS_ONLY_PATTERN,
                  )}
                  {recipientInput(
                    'lastName',
                    isInternationalBank ? '英文姓氏' : '姓氏',
                    isInternationalBank
                      ? ENGLISH_BANK_NAME_PATTERN
                      : ENGLISH_LETTERS_ONLY_PATTERN,
                  )}
                </>
              )}
            </div>
            {personalKind === 'BANK' && (
              <>
                <Field label="電話區號">
                  <SelectField
                    value={recipientForm.watch('phoneCountryCode')}
                    options={PHONE_CODE_OPTIONS}
                    onValueChange={(v) =>
                      recipientForm.setValue('phoneCountryCode', v)
                    }
                  />
                </Field>
                {recipientInput(
                  'phoneNumber',
                  '聯繫電話（手機號）',
                  /^\d{6,14}$/,
                )}
                {recipientInput('address', '地址（選填）', undefined, false)}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {personalKind === 'E_WALLET' ? (
              <>
                <p>
                  選擇要開通的收款方式：
                  {ewalletMethodsForCountry(recipientValues?.country)
                    .map((method) => method.label)
                    .join('、')}
                </p>
                <p className="font-medium">
                  到賬幣種：
                  {
                    EWALLET_METHODS.find((item) => item.value === ewalletMethod)
                      ?.currency
                  }
                </p>
                {accountInput(
                  'accountNo',
                  EWALLET_METHODS.find((item) => item.value === ewalletMethod)
                    ?.fieldLabel || '收款賬號',
                )}
                <label className="flex gap-2">
                  <input
                    type="checkbox"
                    {...accountForm.register('isDefault')}
                  />
                  是否作為默認收款賬戶
                </label>
              </>
            ) : (
              <>
                {isInternationalBank && (
                  <>
                    {accountInput('bankName', '銀行名稱')}
                    {accountInput(
                      'bankCode',
                      'SWIFT/BIC 代碼',
                      /^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/,
                    )}
                  </>
                )}
                {accountInput(
                  'bankAccountNo',
                  '銀行賬號',
                  isInternationalBank ? undefined : /^\d{16,19}$/,
                )}
                {!isInternationalBank &&
                  accountInput(
                    'certNumber',
                    '身份證號',
                    /^(\d{15}|\d{17}[\dX])$/,
                  )}
              </>
            )}
          </div>
        )}
      </Modal>
      <Modal
        title={`確定要刪除此${deletingAccount?.personal ? '個人提款' : '銀行'}賬戶嗎？`}
        open={!!deletingAccount}
        onOpenChange={(open) => {
          if (!open) setDeletingAccount(null)
        }}
        footer={
          <Button
            variant="destructive"
            onClick={async () => {
              if (!deletingAccount) return
              await (deletingAccount.personal
                ? handleDeletePersonalAccount(deletingAccount.id)
                : handleDelete(deletingAccount.id))
              setDeletingAccount(null)
            }}
          >
            確認刪除
          </Button>
        }
      >
        <p>刪除後無法使用此賬戶提交新的結款。</p>
      </Modal>
    </div>
  )
}
